import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import ReCAPTCHA from 'react-google-recaptcha';
import { captureLeadEmail } from '../utils/captureLeadEmail';
import { GoogleLogin, GoogleOAuthProvider } from '@react-oauth/google';
import PageSEO from './PageSEO';

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';

function RegisterInner() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const locParams = new URLSearchParams(window.location.search);
  const forcedType = locParams.get('type');
  const redirectAfter = locParams.get('redirect');
  const [userType, setUserType] = useState(forcedType === 'tenant' ? 'tenant' : 'landlord');
  const [companyName, setCompanyName] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  // Kept as state vars but removed from form
  const [phone] = useState('');
  const [addressLine1] = useState('');
  const [city] = useState('');
  const [province] = useState('');
  const [postalCode] = useState('');
  const [country] = useState('Canada');
  const [dateOfBirth] = useState('');
  const [error, setError] = useState(null);
  const [registered, setRegistered] = useState(false);
  const [infoMessage, setInfoMessage] = useState('');
  const [captchaToken, setCaptchaToken] = useState('');
  const [website, setWebsite] = useState(''); // honeypot
  const [referralCode, setReferralCode] = useState('');
  const [referralPartner, setReferralPartner] = useState(null);
  const [signupPromo, setSignupPromo] = useState('');
  const [tosAccepted, setTosAccepted] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();
  const EMAIL_STORAGE_KEY = 'rentual_register_email';
  const siteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '';

  useEffect(() => {
    const stored = localStorage.getItem(EMAIL_STORAGE_KEY);
    if (stored) {
      setEmail((prev) => prev || stored);
    }
  }, []);

  // Read referral code from URL query param (?ref=CODE)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const ref = params.get('ref');
    if (ref) {
      setReferralCode(ref);
      fetch(`${process.env.REACT_APP_API_BASE_URL}/api/partners/lookup?code=${encodeURIComponent(ref)}`)
        .then((r) => r.ok ? r.json() : null)
        .then((data) => { if (data) setReferralPartner(data); })
        .catch(() => {});
    }
  }, [location.search]);

  const handleEmailBlur = () => {
    captureLeadEmail(email, window.location.pathname, { context: 'register', user_type: userType });
  };

  const handleGoogleSuccess = useCallback(async (credentialResponse) => {
    setError(null);
    setGoogleLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/auth/google`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            credential: credentialResponse.credential,
            userType: userType === 'property_manager' ? 'landlord' : userType,
            account_type: userType === 'property_manager' ? 'property_manager' : undefined,
            company_name: userType === 'property_manager' ? companyName : undefined,
            referral_code: (referralCode && (userType === 'landlord' || userType === 'property_manager')) ? referralCode : undefined,
            promo_code: (signupPromo.trim() && (userType === 'landlord' || userType === 'property_manager')) ? signupPromo.trim() : undefined,
          }),
        }
      );
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Google sign-in failed');
      }
      // Store token and redirect
      localStorage.setItem('token', data.token);
      localStorage.setItem('userType', data.userType);
      if (window.fbq) {
        window.fbq('track', 'CompleteRegistration');
      }
      // Redirect based on user type or back to invite
      if (redirectAfter) {
        navigate(redirectAfter);
      } else if (data.userType === 'tenant') {
        navigate('/tenant/dashboard');
      } else {
        navigate('/landlord/dashboard/v2');
      }
    } catch (err) {
      setError(err.message);
      console.error('Google auth error:', err);
    } finally {
      setGoogleLoading(false);
    }
  }, [userType, companyName, referralCode, signupPromo, navigate, redirectAfter]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!siteKey) {
      setError('Captcha is not configured. Please try again later.');
      return;
    }

    if (siteKey && !captchaToken) {
      setError('Please complete the captcha to continue.');
      return;
    }

    if (password.length < 8 || !/[A-Z]/.test(password) || !/[0-9]/.test(password)) {
      setError('Password must be at least 8 characters and include 1 uppercase letter and 1 number.');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    if (!tosAccepted) {
      setError('You must agree to the Terms of Service and Privacy Policy to register.');
      return;
    }

    if (userType === 'property_manager' && (!companyName || companyName.trim().length < 2)) {
      setError('Please enter your company name.');
      return;
    }

    try {
      const isPropertyManager = userType === 'property_manager';
      const registerType = isPropertyManager ? 'landlord' : userType;
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/${registerType}s/register`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email,
            password,
            first_name: firstName,
            last_name: lastName,
            phone: phone || null,
            address_line1: addressLine1 || null,
            city: city || null,
            province: province || null,
            postal_code: postalCode || null,
            country: country || null,
            date_of_birth: dateOfBirth || null,
            captchaToken,
            website, // honeypot
            ...(isPropertyManager ? { account_type: 'property_manager', company_name: companyName } : {}),
            ...((referralCode && (userType === 'landlord' || isPropertyManager)) ? { referral_code: referralCode } : {}),
            ...((signupPromo.trim() && (userType === 'landlord' || isPropertyManager)) ? { promo_code: signupPromo.trim() } : {})
          }),
        }
      );
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Registration failed');
      }
      localStorage.setItem(EMAIL_STORAGE_KEY, email);

      // Record ToS acceptance if token returned
      if (data.token) {
        try {
          await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/auth/accept-terms`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${data.token}`
            },
            body: JSON.stringify({ consents: ['terms_of_service', 'privacy_policy'] })
          });
        } catch (tosErr) {
          // Non-blocking: consent will be prompted on first login if this fails
          console.warn('ToS acceptance recording failed:', tosErr);
        }
      }

      setInfoMessage(
        'A verification email has been sent. Please check your inbox to verify your account.'
      );
      if (window.fbq) {
        window.fbq('track', 'CompleteRegistration');
      }
      setCaptchaToken('');
      setRegistered(true);
    } catch (err) {
      setError(err.message);
      console.error('Registration error:', err);
    }
  };

  const handleResend = async () => {
    setError(null);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/verify-email/resend`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email, userType }),
        }
      );
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Error resending verification email');
      }
      setInfoMessage(data.message);
    } catch (err) {
      setError(err.message);
      console.error('Resend verification error:', err);
    }
  };

  return (
    <main className="App-main">
      <PageSEO title="Sign Up for Rentual | Start Screening Tenants" description="Create your free Rentual account. Screen tenants, manage properties, collect rent, and generate leases. No credit card required." path="/register" />
      <h2>Register for rentual.co</h2>
      {registered ? (
        <div className="verification-message">
          {error && <p className="error-message">{error}</p>}
          {infoMessage && <p className="info-message">{infoMessage}</p>}
          {!email && (
            <div className="form-group" style={{ marginTop: '0.75rem' }}>
              <label htmlFor="resendEmail">Email used at signup</label>
              <input
                type="email"
                id="resendEmail"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onBlur={handleEmailBlur}
                required
              />
            </div>
          )}
          <button onClick={handleResend} className="secondary-button" disabled={!email}>
            Resend Verification Email
          </button>
          <p>
            Once verified, you can <Link to="/login">login here</Link>.
          </p>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="auth-form">
          {referralPartner && (
            <div className="info-message" style={{ marginBottom: '1rem', padding: '0.75rem', borderRadius: '6px', background: '#f0f9ff', border: '1px solid #bae6fd' }}>
              Referred by <strong>{referralPartner.company || referralPartner.name}</strong>
            </div>
          )}
          {error && <p className="error-message">{error}</p>}

          {/* Google OAuth — only show if configured */}
          {GOOGLE_CLIENT_ID && (
            <>
              <div style={{
                display: 'flex', justifyContent: 'center', marginBottom: '0.5rem',
                opacity: googleLoading ? 0.6 : 1, pointerEvents: googleLoading ? 'none' : 'auto',
                overflow: 'hidden'
              }}>
                <GoogleLogin
                  onSuccess={handleGoogleSuccess}
                  onError={() => setError('Google sign-in failed. Please try again.')}
                  text="continue_with"
                  shape="pill"
                  size="large"
                  width="360"
                  theme={document.body.getAttribute('data-theme') === 'dark' ? 'filled_black' : 'outline'}
                  logo_alignment="center"
                />
              </div>
              {googleLoading && <p className="status-text" style={{ textAlign: 'center', margin: '0.25rem 0 0.5rem', fontSize: '0.85rem' }}>Signing in with Google...</p>}
              <p className="status-text" style={{ textAlign: 'center', fontSize: '0.72rem', margin: '0.25rem 0 0.75rem', lineHeight: 1.5 }}>
                By continuing, you agree to our{' '}
                <Link to="/terms" target="_blank" rel="noopener noreferrer">Terms</Link>
                {' '}and{' '}
                <Link to="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</Link>.
              </p>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', margin: '0.5rem 0' }}>
                <div style={{ flex: 1, height: '1px', background: 'var(--border-color, #e5e7eb)' }} />
                <span style={{ fontSize: '0.82rem', color: 'var(--light-text-color)', whiteSpace: 'nowrap' }}>or register with email</span>
                <div style={{ flex: 1, height: '1px', background: 'var(--border-color, #e5e7eb)' }} />
              </div>
            </>
          )}

          {/* Honeypot — hidden from humans, bots fill it */}
          <div style={{ position: 'absolute', left: '-9999px', opacity: 0, height: 0, overflow: 'hidden' }} aria-hidden="true" tabIndex={-1}>
            <label htmlFor="website">Website</label>
            <input type="text" id="website" name="website" value={website} onChange={e => setWebsite(e.target.value)} autoComplete="off" tabIndex={-1} />
          </div>

          <div className="form-group">
            <label htmlFor="firstName">First name</label>
            <input
              type="text"
              id="firstName"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="lastName">Last name</label>
            <input
              type="text"
              id="lastName"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onBlur={handleEmailBlur}
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
            <p className="status-text" style={{ marginTop: '0.35rem' }}>Minimum 8 characters, 1 uppercase letter, 1 number.</p>
          </div>
          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm password</label>
            <input
              type="password"
              id="confirmPassword"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
            />
          </div>
          {forcedType ? (
            <div className="form-group">
              <label>Registering as:</label>
              <p style={{ fontWeight: 600, margin: '0.25rem 0 0' }}>{forcedType === 'tenant' ? 'Tenant' : 'Landlord'}</p>
            </div>
          ) : (
            <div className="form-group">
              <label htmlFor="userType">Register as:</label>
              <select
                id="userType"
                value={userType}
                onChange={(e) => setUserType(e.target.value)}
              >
                <option value="landlord">Landlord</option>
                <option value="property_manager">Property Manager</option>
                <option value="tenant">Tenant</option>
              </select>
            </div>
          )}
          {userType === 'property_manager' && (
            <div className="form-group">
              <label htmlFor="companyName">Company name</label>
              <input
                type="text"
                id="companyName"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                placeholder="Your property management company"
                required
              />
            </div>
          )}
          {(userType === 'landlord' || userType === 'property_manager') && (
            <>
              {referralCode && (
                <div className="form-group">
                  <label htmlFor="referralCode">Referral code</label>
                  <input
                    type="text"
                    id="referralCode"
                    value={referralCode}
                    onChange={(e) => setReferralCode(e.target.value)}
                    placeholder="Referral code"
                    readOnly={!!new URLSearchParams(location.search).get('ref')}
                  />
                </div>
              )}
              <div className="form-group">
                <label htmlFor="signupPromo">Promo code (optional)</label>
                <input
                  type="text"
                  id="signupPromo"
                  value={signupPromo}
                  onChange={(e) => setSignupPromo(e.target.value)}
                  placeholder="Enter promo code"
                />
              </div>
            </>
          )}
          <div className="form-group" style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}>
            <input
              type="checkbox"
              id="tosAccepted"
              checked={tosAccepted}
              onChange={(e) => setTosAccepted(e.target.checked)}
              style={{ marginTop: '0.25rem', accentColor: '#28b6c8' }}
            />
            <label htmlFor="tosAccepted" className="status-text" style={{ margin: 0, cursor: 'pointer' }}>
              I agree to the{' '}
              <Link to="/terms" target="_blank" rel="noopener noreferrer">Terms of Service</Link>
              {' '}and{' '}
              <Link to="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</Link>
            </label>
          </div>
          {siteKey ? (
            <div className="form-group captcha-group">
              <ReCAPTCHA
                sitekey={siteKey}
                onChange={(token) => setCaptchaToken(token || '')}
                theme={document.body.getAttribute('data-theme') === 'dark' ? 'dark' : 'light'}
              />
            </div>
          ) : (
            <div className="form-status error">
              Captcha is not configured. Set REACT_APP_RECAPTCHA_SITE_KEY and reload.
            </div>
          )}
          <button type="submit" className="primary-button">
            Register
          </button>
          <p className="status-text" style={{ textAlign: 'center', marginTop: '0.5rem', fontSize: '0.82rem', color: 'var(--light-text-color)' }}>
            No credit card required · Free to start
          </p>
        </form>
      )}
      <div className="status-text" style={{ marginTop: '1rem' }}>
        <p><strong>rentual.co includes:</strong></p>
        <ul>
          <li>Viewing scheduling with instant confirmations</li>
          <li>Automated lease renewals + tenant acceptance</li>
          <li>Maintenance tracking and secure documents</li>
        </ul>
      </div>
      <p>
        Already have an account? <Link to="/login">Login here</Link>
      </p>
    </main>
  );
}

function Register() {
  if (GOOGLE_CLIENT_ID) {
    return (
      <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
        <RegisterInner />
      </GoogleOAuthProvider>
    );
  }
  return <RegisterInner />;
}

export default Register;
